import React, { useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { Box, useTheme, Typography } from "@mui/material";

import styles from "./sideBar.module.css";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { useAppTheme } from "../../themes/appThemeProvider";
import { callAPI } from "../../../functions/api";
import { useUser } from "../../../functions/userContext";
import { toast } from "react-toastify";

// ⬇️ forwardRef used here
export const SideBar = forwardRef(({ children, childIcons }, ref) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { user, toolsOpen: expanded, setToolsOpen, checkAccess, ROLE_CONFIG } = useUser();

  const sidebarRef = useRef();
  const { mode, toggleTheme } = useAppTheme();
  const isDarkMode = mode === "dark";

  // ⬇️ Expose open/close/toggle to parent
  useImperativeHandle(ref, () => ({
    open: () => setToolsOpen(true),
    close: () => setToolsOpen(false),
    toggle: () => setToolsOpen((prev) => !prev),
  }));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expanded && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setToolsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [expanded, setToolsOpen]);

  return (
    <Box
      component="div"
      ref={sidebarRef}
      className={`${styles.sidebar} ${expanded ? styles.expanded : styles.collapsed}`}
    >
      <Box
        component="div"
        className={`${styles.sidebar} ${expanded ? styles.expanded : styles.collapsed}`}
      >
        <Box
          component="div"
          className={styles.content}
          sx={{
            opacity: expanded ? 1 : 0,
            transition: "opacity 0.3s ease",
            pointerEvents: expanded ? "auto" : "none",
          }}
        >
          <Box className={styles.container}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
                padding: 0,
                margin: 0,
                width: "100%",
                paddingBottom: "20px",
                marginBottom: "15px",
                borderBottom: `1px solid ${isDarkMode ? "#333" : "#ccc"}`,
              }}
            >
              <Typography variant="subtitle2" className={styles.sectionTitle}>
                Theme
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  borderRadius: "10px",
                  flex: 1,
                  overflow: "hidden",
                  backgroundColor: isDarkMode ? "#2a2a2a" : "#f9f9f9",
                  boxShadow: isDarkMode ? "0 1px 3px rgba(0,0,0,0.4)" : "0 1px 3px rgba(0,0,0,0.1)",
                  border: isDarkMode ? "1px solid #333" : "1px solid #ddd",
                }}
              >
                <Box
                  onClick={toggleTheme}
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    color: !isDarkMode ? "#000" : "#aaa",
                    backgroundColor: !isDarkMode ? "#e0e0e0" : "transparent",
                    fontWeight: !isDarkMode ? 600 : 400,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: !isDarkMode ? "#d5d5d5" : "#333",
                    },
                    borderRight: isDarkMode ? "1px solid #444" : "1px solid #ccc",
                  }}
                >
                  <LightModeIcon fontSize="small" />
                  <Typography variant="body2">Light</Typography>
                </Box>

                <Box
                  onClick={toggleTheme}
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    color: isDarkMode ? "#fff" : "#555",
                    backgroundColor: isDarkMode ? "#3a3a3a" : "transparent",
                    fontWeight: isDarkMode ? 600 : 400,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#444" : "#e0e0e0",
                    },
                  }}
                >
                  <DarkModeIcon fontSize="small" />
                  <Typography variant="body2">Dark</Typography>
                </Box>
              </Box>
            </Box>

            {children}

            {checkAccess([...ROLE_CONFIG.admin]) && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  paddingBottom: "10px",
                  width: "100%",
                  px: 2,
                  borderTop: `1px solid ${isDarkMode ? "#333" : "#ccc"}`,
                  paddingTop: "10px",
                }}
              >
                <Box
                  onClick={async () => {
                    await callAPI("admin/caching/reset", "POST", null).then((res) => {
                      if (res.status === 200) {
                        toast.success("Server cache cleared successfully!");
                        setTimeout(() => {
                          window.location.reload();
                        }, 1000);
                      } else {
                        toast.error("Failed to clear server cache.");
                        setTimeout(() => {
                          window.location.reload();
                        }, 1000);
                      }
                    });
                  }}
                  sx={{
                    flex: 1,
                    display: "flex",
                    gap: 1,
                    cursor: "pointer",
                    color: isDarkMode ? "#fff" : "#555",
                    backgroundColor: isDarkMode ? "#3a3a3a" : "transparent",
                    fontWeight: isDarkMode ? 600 : 400,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: isDarkMode ? "#444" : "#e0e0e0",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      px: 2,
                      py: 1,
                      textAlign: "center",
                      flex: 1,
                      textTransform: "uppercase",
                      fontWeight: "bold",
                    }}
                    variant="body2"
                  >
                    Clear Cache
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
